/**
 * The things in an encounter that are shot at but do not fly: observation
 * balloons, Zeppelins, and the structures a bombing raid is flown against.
 *
 * They are deliberately *not* modelled as aircraft. `SimState` is an aeroplane
 * — a wing, a rotary engine, a pilot who can be wounded, a belt that can jam —
 * and none of that is true of a tethered gasbag, let alone of a railway yard.
 * So an encounter carries a second, much smaller list: a pose, an integrity
 * value, and a fire.
 *
 * What each *kind* is — its geometry, its toughness, whether it drifts — lives
 * in `target-profiles.ts` and is re-exported from here, so a consumer that only
 * wants "the targets layer" still has one import. This file is the mechanics:
 * create one, drift it, damage it, burn it out.
 *
 * The airborne/ground split shows up in exactly two places, and both are noted
 * where they happen: a burning gasbag sinks and a burning building does not, and
 * a gasbag that reaches the ground is finished while a building starts there.
 */

import { attitudeFromAngles, scaleVec3, rotateVec3, BODY_FORWARD, type Quat, type Vec3 } from "../flight/math3d.ts";
import { airshipStationBelts } from "./airship-guns.ts";
import type { CombatBody } from "./geometry.ts";
import type { EncounterSide } from "./encounter-sides.ts";
import { routePosition, routeSpent, type GroundRoute } from "./ground-route.ts";
import {
  AMMUNITION_TYPES,
  TARGET_KINDS,
  TARGET_PROFILES,
  targetProfileOf,
  type AmmunitionType,
  type TargetKind,
  type TargetProfile,
} from "./target-profiles.ts";

export {
  AIRBORNE_TARGET_KINDS,
  AMMUNITION_TYPES,
  DEFENCE_TARGET_KINDS,
  GROUND_TARGET_KINDS,
  MOBILE_GROUND_TARGET_KINDS,
  TARGET_KINDS,
  TARGET_PROFILES,
  groundTargetPosition,
  isGroundTargetKind,
  isStrategicTargetKind,
  targetProfileOf,
} from "./target-profiles.ts";
export type {
  AirborneTargetKind,
  AmmunitionType,
  DefenceTargetKind,
  GroundTargetKind,
  MobileGroundTargetKind,
  TargetClass,
  TargetKillCategory,
  TargetKind,
  TargetProfile,
} from "./target-profiles.ts";
export { drawGroundRoute, groundRoute, routePosition, routeSpent } from "./ground-route.ts";
export type { DrawnRoute, GroundRoute, RouteDrawOptions } from "./ground-route.ts";

export interface EncounterTargetSpec {
  readonly id: string;
  readonly kind: TargetKind;
  readonly side: EncounterSide;
  readonly position: Vec3;
  /** Heading it drifts along; ignored for a tethered balloon or anything on the ground. */
  readonly headingRad?: number;
  /** Overrides the profile's display name, e.g. `"Drachen 3"`. */
  readonly label?: string;
  /**
   * A rack, for the one target kind that ever had one: a Zeppelin inbound on a
   * raid. It is the same `BombLoadSpec` an aeroplane carries and it is flown
   * through the same release solver — an airship is a target here rather than a
   * flight, but there is nothing about dropping a bomb that needs a pilot.
   */
  readonly bombs?: { readonly count: number; readonly targetIds: readonly string[] };
  /**
   * The two-point path a train runs. Present only for a route-following kind;
   * when it is given, it overrides `position` and `headingRad`, because the
   * original's class-5 constructor derives both from the route rather than
   * carrying them separately.
   */
  readonly route?: GroundRoute;
  /** How far along `route` it already is when the encounter opens. */
  readonly routeTravelledM?: number;
}

export interface EncounterTarget {
  readonly id: string;
  readonly kind: TargetKind;
  readonly side: EncounterSide;
  readonly label: string;
  readonly position: Vec3;
  readonly velocity: Vec3;
  readonly orientation: Quat;
  /** 1 intact, 0 alight. */
  readonly integrity: number;
  readonly burning: boolean;
  readonly burnSeconds: number;
  readonly destroyed: boolean;
  readonly hitsTaken: number;
  /** Bombs that have gone off close enough to take integrity off it. */
  readonly bombsTaken: number;
  /** A Zeppelin's own rack; zero for everything else. Mirrors `CombatAircraft`. */
  readonly bombsRemaining: number;
  readonly bombsReleased: number;
  readonly bombTargetIds: readonly string[];
  readonly lastReleaseTick: number | null;
  /** Who last put rounds or bombs into it — the credit once it finishes burning. */
  readonly lastAttackerId: string | null;
  /**
   * Rounds left in each of its own gun stations' belts — `MG.DAT`'s 120 apiece
   * for a Zeppelin, and **empty for every other kind**, which is what keeps a
   * balloon, a factory and a searchlight as inert as they were before the
   * airship learned to shoot (`airship-guns.ts`, `airship-pass.ts`).
   *
   * Per station rather than pooled, unlike `flight/guns.ts`'s single
   * `GunState`: the original seeds a separate counter per station
   * (`pobj.c+0x317`) and an airship's four gunners genuinely do run dry
   * independently, since each covers a different quarter of the sky.
   */
  readonly gunRoundsRemaining: readonly number[];
  /** Running total it has fired, which is what gives each round its `roundIndex`. */
  readonly gunRoundsFired: number;
  /**
   * Each station's standing aiming mistake, azimuth and elevation interleaved —
   * `[az0, el0, az1, el1, …]`, radians. The gunner's-hand half of
   * `airship-pass.ts`'s aim model: a bias he holds for a burst, the way an AI
   * pilot holds one for a manoeuvre, as distinct from the per-round graze
   * scatter that sits on top of it.
   */
  readonly gunAimBias: readonly number[];
  /** Which burst-length block of `gunRoundsFired` `gunAimBias` was drawn for. `-1` before the first shot. */
  readonly gunAimEpoch: number;
  /** The route it runs, or `null` for everything that does not run one. */
  readonly route: GroundRoute | null;
  /** Distance covered along `route`. */
  readonly routeTravelledM: number;
  /**
   * It reached the end of its route and stopped. **This is not a kill** — the
   * original keeps the two on different bits (`obj[0x22] |= 0x8000` for arriving,
   * `obj[0x20] |= 0x40` for being destroyed), and nothing is credited for a
   * train that simply got where it was going.
   */
  readonly arrived: boolean;
}

export function createTarget(spec: EncounterTargetSpec): EncounterTarget {
  const kindProfile = targetProfileOf(spec.kind);
  // A route-follower takes its pose from the route, the way the original's
  // class-5 constructor does: it stores both endpoints and derives the bearing
  // from them rather than reading a heading out of the record.
  const route = kindProfile.followsRoute ? (spec.route ?? null) : null;
  const routeTravelledM = route ? Math.min(spec.routeTravelledM ?? 0, route.lengthM) : 0;
  const orientation = attitudeFromAngles(route ? route.headingRad : (spec.headingRad ?? 0), 0, 0);
  return {
    id: spec.id,
    kind: spec.kind,
    side: spec.side,
    label: spec.label ?? kindProfile.displayName,
    position: route ? routePosition(route, routeTravelledM) : spec.position,
    velocity: scaleVec3(rotateVec3(orientation, BODY_FORWARD), kindProfile.driftSpeedMps),
    orientation,
    integrity: 1,
    burning: false,
    burnSeconds: 0,
    destroyed: false,
    hitsTaken: 0,
    bombsTaken: 0,
    bombsRemaining: spec.bombs?.count ?? 0,
    bombsReleased: 0,
    bombTargetIds: spec.bombs?.targetIds ?? [],
    lastReleaseTick: null,
    lastAttackerId: null,
    gunRoundsRemaining: airshipStationBelts(spec.kind),
    gunRoundsFired: 0,
    gunAimBias: [],
    gunAimEpoch: -1,
    route,
    routeTravelledM,
    arrived: route ? routeSpent(route, routeTravelledM) : false,
  };
}

export function targetBody(target: EncounterTarget): CombatBody {
  return { position: target.position, velocity: target.velocity, orientation: target.orientation };
}

/** The gunnery-side view of a target — the same shape an aeroplane presents. */
export function targetGunneryEntry(target: EncounterTarget): {
  id: string;
  body: CombatBody;
  targetSizeRaw: number;
} {
  return { id: target.id, body: targetBody(target), targetSizeRaw: targetProfileOf(target.kind).targetSizeRaw };
}

/** Marks the hit and, if that spent the last of its integrity, sets it alight. */
function wound(target: EncounterTarget, marked: EncounterTarget, lost: number): EncounterTarget {
  if (target.destroyed || target.burning) return marked;
  const integrity = Math.max(0, target.integrity - lost);
  return { ...marked, integrity, burning: integrity <= 0 };
}

/** Applies `hits` rounds of `ammunition` from `shooterId`, igniting the target when its integrity runs out. */
export function damageTarget(
  target: EncounterTarget,
  hits: number,
  ammunition: AmmunitionType,
  shooterId: string | null = null,
): EncounterTarget {
  if (hits <= 0) return target;
  const marked = { ...target, hitsTaken: target.hitsTaken + hits, lastAttackerId: shooterId ?? target.lastAttackerId };
  return wound(target, marked, targetProfileOf(target.kind).damagePerHit[ammunition] * hits);
}

/**
 * Sets a gasbag alight outright, with no integrity left to spend — what happens
 * when somebody flies into one (AD-017 §4).
 *
 * A ram is not a big hit; the original puts it on its own ladder, `500` against
 * an ignition threshold of `200`, which no amount of arithmetic ever fails to
 * clear. So this takes no magnitude: it is the ignition itself. It respects the
 * same "already gone" guard `wound` does, because the credit for a Zeppelin
 * belongs to whoever set it burning and not to the next pilot who flies through
 * the fire.
 */
export function igniteTarget(target: EncounterTarget, attackerId: string | null = null): EncounterTarget {
  if (target.destroyed || target.burning) return target;
  return {
    ...target,
    integrity: 0,
    burning: true,
    lastAttackerId: attackerId ?? target.lastAttackerId,
  };
}

/**
 * Applies one bomb's share of integrity. `share` is the blast falloff
 * (`bombs.ts`) already multiplied by the profile's own toughness, so this
 * function stays the same one line the gunfire path is.
 */
export function bombTarget(target: EncounterTarget, share: number, bomberId: string | null = null): EncounterTarget {
  if (share <= 0) return target;
  const marked = {
    ...target,
    bombsTaken: target.bombsTaken + 1,
    lastAttackerId: bomberId ?? target.lastAttackerId,
  };
  return wound(target, marked, share);
}

/**
 * One tick of a target: drift, or — once it is alight — burn out. A burning
 * gasbag stops making way and sinks, which is what puts the flamer on a visibly
 * different track from the intact one beside it; a burning building stays
 * exactly where it was built and simply stops being a target when the fire has
 * had its `burnSeconds`.
 */
export function advanceTarget(target: EncounterTarget, dt: number): EncounterTarget {
  if (target.destroyed) return target;
  const kindProfile = targetProfileOf(target.kind);
  if (target.burning) return burn(target, kindProfile, dt);
  if (target.route) return runRoute(target, target.route, kindProfile, dt);
  if (kindProfile.driftSpeedMps === 0) return target;
  return {
    ...target,
    position: {
      x: target.position.x + target.velocity.x * dt,
      y: target.position.y + target.velocity.y * dt,
      z: target.position.z + target.velocity.z * dt,
    },
  };
}

/**
 * One tick of a train, transcribed from `s\intel\move.c` (file `0x11621`): add
 * `speed * dt` to the distance-travelled accumulator, and either place the train
 * at the interpolated point or — if the route is spent — stop it where it is.
 *
 * A train that has arrived is *retired, not killed*: `arrived` goes true, its
 * velocity goes to zero, and `destroyed` is untouched, so nobody is credited
 * for a train that simply finished its journey.
 */
function runRoute(
  target: EncounterTarget,
  route: GroundRoute,
  kindProfile: TargetProfile,
  dt: number,
): EncounterTarget {
  if (target.arrived) return target;
  const routeTravelledM = target.routeTravelledM + kindProfile.driftSpeedMps * dt;
  if (routeSpent(route, routeTravelledM)) {
    return {
      ...target,
      position: route.end,
      velocity: { x: 0, y: 0, z: 0 },
      routeTravelledM: route.lengthM,
      arrived: true,
    };
  }
  return { ...target, position: routePosition(route, routeTravelledM), routeTravelledM };
}

function burn(target: EncounterTarget, kindProfile: TargetProfile, dt: number): EncounterTarget {
  const burnSeconds = target.burnSeconds + dt;
  const spent = burnSeconds >= kindProfile.burnSeconds;
  if (kindProfile.targetClass === "ground") {
    // A structure is already on the ground: the only clock that can finish it is
    // the fire's own. Reading the gasbag rule here would destroy it on the tick
    // it caught, since its altitude is already zero.
    return { ...target, burnSeconds, destroyed: spent };
  }
  const y = Math.max(0, target.position.y - kindProfile.burnDescentMps * dt);
  return {
    ...target,
    position: { x: target.position.x, y, z: target.position.z },
    velocity: { x: 0, y: -kindProfile.burnDescentMps, z: 0 },
    burnSeconds,
    destroyed: spent || y <= 0,
  };
}

/** Every kind's profile, in `TARGET_KINDS` order — for a test that wants the whole catalog. */
export const TARGET_PROFILE_LIST: readonly TargetProfile[] = TARGET_KINDS.map((kind) => TARGET_PROFILES[kind]);

/** The ammunition a shooter with nothing declared is carrying. */
export const DEFAULT_AMMUNITION: AmmunitionType = AMMUNITION_TYPES[0];
